/** Single Sign-On (SAML 2.0 / OIDC) administration page. */
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import {
  type SsoConnection,
  useCreateSsoConnection,
  useDeleteSsoConnection,
  useRoles,
  useSsoConnections,
  useUpdateSsoConnection,
} from "@/features/admin/identity/queries";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import { Badge, Card } from "@/shared/ui/primitives";

interface SsoFormValues extends Record<string, unknown> {
  name: string;
  protocol: string;
  provider_id: string;
  email_domains: string;
  enforce: boolean;
  jit_provisioning: boolean;
  default_role_key: string;
  is_active: boolean;
  // SAML
  idp_entity_id: string;
  idp_sso_url: string;
  idp_x509cert: string;
  // OIDC
  server_url: string;
  client_id: string;
  client_secret: string;
  // Optional JSON group -> role mapping
  role_mappings_json: string;
  sync_roles_on_login: boolean;
}

function asStr(val: unknown): string {
  return typeof val === "string" ? val : "";
}

function getInitialValues(conn: SsoConnection | null, tenantSlug: string): SsoFormValues {
  const cfg = (conn?.config ?? {}) as Record<string, unknown>;
  const idp = (cfg.idp ?? {}) as Record<string, unknown>;
  const roleMappings = cfg.role_mappings ? JSON.stringify(cfg.role_mappings) : "";
  return {
    name: conn?.name ?? "",
    protocol: conn?.protocol ?? "saml",
    provider_id: conn?.provider_id ?? (tenantSlug ? `${tenantSlug}-saml` : ""),
    email_domains: (conn?.email_domains ?? []).join(", "),
    enforce: conn?.enforce ?? false,
    jit_provisioning: conn?.jit_provisioning ?? true,
    default_role_key: conn?.default_role_key ?? "requester",
    is_active: conn?.is_active ?? true,
    idp_entity_id: asStr(cfg.idp_entity_id ?? idp.entity_id),
    idp_sso_url: asStr(cfg.idp_sso_url ?? idp.sso_url),
    idp_x509cert: asStr(cfg.idp_x509cert ?? idp.x509cert),
    server_url: asStr(cfg.server_url),
    client_id: asStr(cfg.client_id),
    client_secret: "",
    role_mappings_json: roleMappings,
    sync_roles_on_login: Boolean(cfg.sync_roles_on_login),
  };
}

/** Dialog to create or edit an SSO connection (SAML 2.0 or OIDC). */
export function SsoConnectionDialog({
  connection,
  onClose,
}: {
  connection: SsoConnection | null;
  onClose: () => void;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const { me, tenantSlug } = useAuth();
  const isPlatformAdmin = Boolean(me?.is_platform_admin);
  const roles = useRoles();
  const create = useCreateSsoConnection();
  const update = useUpdateSsoConnection();
  const [jsonError, setJsonError] = useState<string | null>(null);
  const form = useForm<SsoFormValues>({
    initial: getInitialValues(connection, tenantSlug),
    idPrefix: "sso",
  });

  const fillMockSamlDefaults = (): void => {
    form.set("name", form.values.name || "MockSAML");
    form.set("protocol", "saml");
    if (!connection && !form.values.provider_id) {
      form.set("provider_id", `${tenantSlug || "demo"}-mocksaml`);
    }
    form.set("idp_entity_id", "https://saml.example.com/entityid");
    form.set("idp_sso_url", "https://mocksaml.com/api/saml/sso");
  };

  const submit = async (): Promise<void> => {
    setJsonError(null);
    let parsedRoleMappings: Record<string, unknown> | undefined;
    if (form.values.role_mappings_json.trim()) {
      try {
        parsedRoleMappings = JSON.parse(form.values.role_mappings_json) as Record<string, unknown>;
      } catch {
        setJsonError(t("sso.invalidRoleMappingsJson"));
        return;
      }
    }

    const config: Record<string, unknown> = isPlatformAdmin
      ? form.values.protocol === "saml"
        ? {
            idp_entity_id: form.values.idp_entity_id.trim(),
            idp_sso_url: form.values.idp_sso_url.trim(),
            idp_x509cert: form.values.idp_x509cert.trim(),
          }
        : {
            server_url: form.values.server_url.trim(),
            client_id: form.values.client_id.trim(),
          }
      : {
          role_mappings: parsedRoleMappings ?? {},
          sync_roles_on_login: form.values.sync_roles_on_login,
        };
    if (isPlatformAdmin && parsedRoleMappings) {
      config.role_mappings = parsedRoleMappings;
      config.sync_roles_on_login = form.values.sync_roles_on_login;
    }

    const domains = form.values.email_domains
      .split(",")
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);

    const ok = await form.submit(async (v) => {
      if (connection) {
        return update.mutateAsync({
          id: connection.id,
          body: {
            name: v.name.trim(),
            ...(isPlatformAdmin ? { email_domains: domains } : {}),
            enforce: v.enforce,
            jit_provisioning: v.jit_provisioning,
            default_role_key: v.default_role_key || null,
            config,
            ...(isPlatformAdmin && v.client_secret.trim()
              ? { client_secret: v.client_secret.trim() }
              : {}),
            is_active: v.is_active,
          },
        });
      }
      return create.mutateAsync({
        name: v.name.trim(),
        protocol: v.protocol,
        provider_id: v.provider_id.trim().toLowerCase(),
        email_domains: domains,
        enforce: v.enforce,
        jit_provisioning: v.jit_provisioning,
        default_role_key: v.default_role_key || null,
        config,
        client_secret: v.client_secret.trim(),
        is_active: v.is_active,
      });
    });

    if (ok) {
      onClose();
    }
  };

  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={connection ? t("sso.editConnection") : t("sso.addConnection")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="sso-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} required />
        </Field>
        <Field id="sso-protocol" label={t("sso.protocol")}>
          <Select
            id="sso-protocol"
            value={form.values.protocol}
            onChange={(e) => form.set("protocol", e.target.value)}
            disabled={Boolean(connection) || !isPlatformAdmin}
          >
            <option value="saml">SAML 2.0</option>
            <option value="oidc">OpenID Connect (OIDC)</option>
          </Select>
        </Field>
        <Field
          id="sso-provider_id"
          label={t("sso.providerId")}
          hint={t("sso.providerIdHint")}
          error={form.errors.provider_id}
        >
          <Input
            {...form.field("provider_id")}
            readOnly={Boolean(connection) || !isPlatformAdmin}
            required
          />
        </Field>
        <Field id="sso-default_role_key" label={t("sso.defaultRole")}>
          <Select
            id="sso-default_role_key"
            value={form.values.default_role_key}
            onChange={(e) => form.set("default_role_key", e.target.value)}
          >
            <option value="">{t("app.none")}</option>
            {(roles.data ?? []).map((r) => (
              <option key={r.key} value={r.key}>
                {(r.name_i18n as Record<string, string>)[i18n.language] ?? r.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Field
        id="sso-email_domains"
        label={t("sso.emailDomains")}
        hint={t("sso.emailDomainsHint")}
        error={form.errors.email_domains}
      >
        <Input
          {...form.field("email_domains")}
          readOnly={!isPlatformAdmin}
          placeholder="rsg.sa, example.com"
        />
      </Field>

      {isPlatformAdmin ? (
        form.values.protocol === "saml" ? (
          <div className="space-y-3 rounded-md border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold">{t("sso.samlSettings")}</span>
              <Button type="button" size="sm" variant="outline" onClick={fillMockSamlDefaults}>
                {t("sso.fillMockSaml")}
              </Button>
            </div>
            <Field id="sso-idp_entity_id" label={t("sso.idpEntityId")}>
              <Input
                {...form.field("idp_entity_id")}
                placeholder="https://saml.example.com/entityid"
                required
              />
            </Field>
            <Field id="sso-idp_sso_url" label={t("sso.idpSsoUrl")}>
              <Input
                {...form.field("idp_sso_url")}
                placeholder="https://mocksaml.com/api/saml/sso"
                required
              />
            </Field>
            <Field id="sso-idp_x509cert" label={t("sso.idpCertificate")}>
              <Textarea
                {...form.field("idp_x509cert")}
                rows={3}
                className="font-mono text-xs"
                placeholder="-----BEGIN CERTIFICATE-----..."
              />
            </Field>
          </div>
        ) : (
          <div className="space-y-3 rounded-md border p-3">
            <span className="text-xs font-semibold">{t("sso.oidcSettings")}</span>
            <Field id="sso-server_url" label={t("sso.oidcIssuerUrl")}>
              <Input
                {...form.field("server_url")}
                placeholder="https://login.microsoftonline.com/<tenant>/v2.0"
                required
              />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field id="sso-client_id" label={t("sso.clientId")}>
                <Input {...form.field("client_id")} required />
              </Field>
              <Field id="sso-client_secret" label={t("sso.clientSecret")}>
                <Input
                  type="password"
                  {...form.field("client_secret")}
                  placeholder={connection?.has_client_secret ? "••••••••" : ""}
                />
              </Field>
            </div>
          </div>
        )
      ) : null}

      <Field
        id="sso-role_mappings_json"
        label={t("sso.roleMappings")}
        hint={t("sso.roleMappingsHint")}
        error={jsonError ?? form.errors.role_mappings_json}
      >
        <Input
          {...form.field("role_mappings_json")}
          className="font-mono text-xs"
          placeholder='{"MMS-Admins": ["admin"], "MMS-Technicians": ["technician"]}'
        />
      </Field>

      <div className="flex flex-wrap gap-4 pt-1">
        <Checkbox
          id="sso-jit_provisioning"
          label={t("sso.jitProvisioning")}
          checked={form.values.jit_provisioning}
          onChange={(e) => form.set("jit_provisioning", e.target.checked)}
        />
        <Checkbox
          id="sso-enforce"
          label={t("sso.enforceSso")}
          checked={form.values.enforce}
          onChange={(e) => form.set("enforce", e.target.checked)}
        />
        <Checkbox
          id="sso-sync_roles"
          label={t("sso.syncRolesOnLogin")}
          checked={form.values.sync_roles_on_login}
          onChange={(e) => form.set("sync_roles_on_login", e.target.checked)}
        />
        <Checkbox
          id="sso-is_active"
          label={t("sso.active")}
          checked={form.values.is_active}
          onChange={(e) => form.set("is_active", e.target.checked)}
        />
      </div>
    </FormDialog>
  );
}

/** Single Sign-On administration screen (`/admin/sso`). */
export function SsoPage(): ReactElement {
  const { t } = useTranslation();
  const { me } = useAuth();
  const isPlatformAdmin = Boolean(me?.is_platform_admin);
  const q = useSsoConnections();
  const remove = useDeleteSsoConnection();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SsoConnection | null>(null);

  if (q.isPending) {
    return <LoadingState />;
  }
  if (q.isError) {
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("sso.title")}
        subtitle={t("sso.subtitle")}
        actions={
          isPlatformAdmin ? (
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              {t("sso.addConnection")}
            </Button>
          ) : null
        }
      />

      {q.data.length === 0 ? (
        <Card className="p-6 text-center text-xs text-muted-foreground">{t("sso.empty")}</Card>
      ) : (
        <div className="space-y-3">
          {q.data.map((conn) => (
            <Card key={conn.id} className="space-y-3 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{conn.name}</span>
                    <Badge tone="info">{conn.protocol.toUpperCase()}</Badge>
                    <Badge tone={conn.is_active ? "success" : "neutral"}>
                      {conn.is_active ? t("sso.active") : t("sso.inactive")}
                    </Badge>
                    {conn.enforce ? <Badge tone="warning">{t("sso.enforced")}</Badge> : null}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t("sso.providerId")}: <code className="font-mono">{conn.provider_id}</code>
                    {conn.email_domains.length > 0
                      ? ` · ${t("sso.emailDomains")}: ${conn.email_domains.join(", ")}`
                      : ""}
                    {conn.default_role_key
                      ? ` · ${t("sso.defaultRole")}: ${conn.default_role_key}`
                      : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditing(conn);
                      setDialogOpen(true);
                    }}
                  >
                    {t("app.edit")}
                  </Button>
                  {isPlatformAdmin ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(conn.id)}
                      disabled={remove.isPending}
                    >
                      {t("app.delete")}
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 rounded-md border bg-muted/30 p-2.5 text-xs sm:grid-cols-2">
                <div>
                  <div className="font-medium text-muted-foreground">{t("sso.acsCallbackUrl")}</div>
                  <code className="break-all font-mono text-[11px]">
                    {conn.acs_or_callback_url}
                  </code>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">{t("sso.spEntityId")}</div>
                  <code className="break-all font-mono text-[11px]">{conn.sp_entity_id}</code>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {dialogOpen ? (
        <SsoConnectionDialog
          connection={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}
